import { useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { useBeforeUnload, useBlocker } from 'react-router-dom';
import { Button, Drawer, Form, Grid, Modal, Spin, Typography, message } from 'antd';
import { Permission, type ClienteResponse, type UpdateClienteDto } from '@aid/shared';
import {
  ClienteForm,
  getClienteFormInitialValues,
  type ClienteFormValues,
} from './components/ClienteForm';
import { useCambioDiaFacturacion, useCliente, useUpdateCliente } from './api/useClientes';
import { useHasPermission } from '../../auth/useHasPermission';
import { useT } from '../../locales';
import { EmptyState } from '../../components/EmptyState';
import dayjs from 'dayjs';
import { formatCurrency } from '../../lib/format';
import { parseDateOnly, toDateOnlyString } from '../../lib/date';
import {
  getCambioDiaMutationErrorReason,
  getClienteMutationErrorReason,
} from './api/clientes-error';

const DIRTY_ACTION = {
  CLOSE: 'close',
  NAVIGATION: 'navigation',
} as const;

type DirtyAction = (typeof DIRTY_ACTION)[keyof typeof DIRTY_ACTION];

const EDITABLE_FORM_FIELDS = [
  'nombre',
  'identificacion',
  'telefonoPrincipal',
  'telefonoReferencia',
  'email',
  'ip',
  'nit',
  'direccion',
  'latitud',
  'longitud',
  'comentarios',
  'diaCobro',
  'estadoCliente',
  'tipoCliente',
  'tipoSuscripcion',
  'fechaNacimiento',
  'fechaCobro',
  'fechaAlta',
] as const;

type EditableFormField = (typeof EDITABLE_FORM_FIELDS)[number];

interface ClienteEditTitleProps {
  cliente: ClienteResponse | undefined;
  clienteId: number;
  editLabel: string;
  codeLabel: string;
}

function ClienteEditTitle({ cliente, clienteId, editLabel, codeLabel }: ClienteEditTitleProps) {
  const name = cliente?.nombre?.trim() || `#${clienteId}`;
  const identifier = cliente?.ip ? `${codeLabel}: ${cliente.ip}` : `#${clienteId}`;

  return (
    <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography.Text strong>{editLabel}</Typography.Text>
      <Typography.Text type="secondary" ellipsis={{ tooltip: true }} style={{ fontSize: 12 }}>
        {name} · {identifier}
      </Typography.Text>
    </div>
  );
}

function normalizeFormValue(value: unknown): string | number | null {
  if (dayjs.isDayjs(value)) return value.format('YYYY-MM-DD');
  if (typeof value === 'string') return value === '' ? null : value;
  if (typeof value === 'number') return value;
  return null;
}

function serializeFormValues(values: Partial<ClienteFormValues>): string {
  return JSON.stringify(
    EDITABLE_FORM_FIELDS.map(
      (field: EditableFormField) => [field, normalizeFormValue(values[field])] as const,
    ),
  );
}

const modalBodyStyle: CSSProperties = {
  maxHeight: 'min(70vh, 680px)',
  overflowY: 'auto',
  padding: '4px 24px 16px',
  scrollbarGutter: 'stable',
};

const drawerBodyStyle: CSSProperties = {
  minHeight: 0,
  overflowY: 'auto',
  padding: '4px 24px 24px',
  scrollbarGutter: 'stable',
};

export interface ClienteEditOverlayProps {
  clienteId: number;
  /**
   * In-memory overlay state owned by the page that renders it (the Cliente
   * 360 detail/tab routes) or the always-open state of the deep-link route
   * (/clientes/:id/edit).
   */
  open: boolean;
  /**
   * Invoked when the overlay must close (cancel, discard, or save success).
   * The parent decides what that means: closing without touching the current
   * tab route (in-memory mode) or navigating back to /clientes/:id (deep link).
   */
  onClose: () => void;
}

/**
 * Client edit overlay (Modal on desktop, Drawer on mobile) shared by the
 * Cliente 360 detail page and the /clientes/:id/edit deep-link route. It owns
 * the form, the dirty snapshot, the navigation blocker, the discard
 * confirmation, and the save flow — WITHOUT owning the URL: opening it from
 * a client tab never changes location.pathname.
 */
export function ClienteEditOverlay({ clienteId, open, onClose }: ClienteEditOverlayProps) {
  const t = useT();
  const { data: cliente, isLoading, error } = useCliente(clienteId);
  const updateCliente = useUpdateCliente();
  const cambioDia = useCambioDiaFacturacion();
  const hasPermission = useHasPermission(Permission.CLIENTES_UPDATE);
  const screens = Grid.useBreakpoint();
  const [form] = Form.useForm<ClienteFormValues>();
  const [isDirty, setIsDirty] = useState(false);
  const [dirtyAction, setDirtyAction] = useState<DirtyAction | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const allowNavigationRef = useRef(false);
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isDirty &&
      !allowNavigationRef.current &&
      (currentLocation.pathname !== nextLocation.pathname ||
        currentLocation.search !== nextLocation.search ||
        currentLocation.hash !== nextLocation.hash),
  );

  useBeforeUnload((event) => {
    if (!isDirty) return;
    event.preventDefault();
    event.returnValue = '';
  });

  const initialFormSnapshot = cliente
    ? serializeFormValues(getClienteFormInitialValues(cliente))
    : null;

  useEffect(() => {
    allowNavigationRef.current = false;
    setIsDirty(false);
    setConfirmOpen(false);
    setDirtyAction(null);
  }, [clienteId]);

  // Reopening must restore FULL dirty protection. After a discard/save the
  // allow flag stays true while this component remains mounted (in-memory
  // mode), so without this reset a reopened dirty form could navigate away
  // silently (the blocker would never activate). The form itself remounts
  // clean on open (destroyOnHidden), so a fresh dirty snapshot applies.
  useEffect(() => {
    if (!open) return;
    allowNavigationRef.current = false;
    setIsDirty(false);
    setConfirmOpen(false);
    setDirtyAction(null);
  }, [open]);

  useEffect(() => {
    if (blocker.state !== 'blocked') return;
    setDirtyAction(DIRTY_ACTION.NAVIGATION);
    setConfirmOpen(true);
  }, [blocker.state]);

  const handleValuesChange = (
    _changedValues: Partial<ClienteFormValues>,
    allValues: ClienteFormValues,
  ) => {
    if (initialFormSnapshot === null) return;
    setIsDirty(serializeFormValues(allValues) !== initialFormSnapshot);
  };

  const requestClose = () => {
    if (updateCliente.isPending) return;
    if (isDirty) {
      setDirtyAction(DIRTY_ACTION.CLOSE);
      setConfirmOpen(true);
      return;
    }
    onClose();
  };

  const handleDiscard = () => {
    const action = dirtyAction;
    setConfirmOpen(false);
    setDirtyAction(null);
    setIsDirty(false);
    allowNavigationRef.current = true;

    if (action === DIRTY_ACTION.NAVIGATION && blocker.state === 'blocked') {
      blocker.proceed();
      return;
    }

    if (action === DIRTY_ACTION.CLOSE) {
      onClose();
    }
  };

  const handleKeepEditing = () => {
    setConfirmOpen(false);
    setDirtyAction(null);
    if (blocker.state === 'blocked') blocker.reset();
  };

  const handleSubmit = async (values: UpdateClienteDto) => {
    if (!cliente) return;

    const aplicarAjuste = form.getFieldValue('aplicarAjuste') === true;
    const fechaInicial = cliente.fechaCobro
      ? (toDateOnlyString(parseDateOnly(cliente.fechaCobro)) ?? null)
      : null;
    const fechaNueva = values.fechaCobro ?? null;
    const usaraAjuste =
      aplicarAjuste && fechaInicial !== null && fechaNueva !== null && fechaNueva !== fechaInicial;

    try {
      if (usaraAjuste) {
        // Two deliberate steps: first the non-financial fields WITHOUT the
        // cursor (so the adjustment's optimistic guard still sees the original
        // date), then the atomic date move + artifact. A failure in the second
        // step leaves the other edits saved and the cursor untouched.
        const restoSinFecha: UpdateClienteDto = { ...values };
        delete restoSinFecha.fechaCobro;
        await updateCliente.mutateAsync({ id: cliente.id, dto: restoSinFecha });
        const resultado = await cambioDia.mutateAsync({
          id: cliente.id,
          dto: {
            fechaCobro: fechaNueva,
            fechaCobroAnterior: fechaInicial,
            aplicarAjuste: true,
          },
        });
        setIsDirty(false);
        allowNavigationRef.current = true;
        message.success(
          resultado.ajuste
            ? t('pages.clientes.ajusteDia.exito', {
                monto: formatCurrency(resultado.ajuste.monto),
              })
            : t('pages.clientes.saveSuccess'),
        );
        onClose();
        return;
      }

      await updateCliente.mutateAsync({ id: cliente.id, dto: values });
      setIsDirty(false);
      allowNavigationRef.current = true;
      message.success(t('pages.clientes.saveSuccess'));
      onClose();
    } catch (error: unknown) {
      const reason =
        getCambioDiaMutationErrorReason(error, t) ?? getClienteMutationErrorReason(error, t);
      message.error(
        reason
          ? t('pages.clientes.saveValidationError', { reason })
          : t('pages.clientes.saveError'),
      );
    }
  };

  const guardando = updateCliente.isPending || cambioDia.isPending;

  const content = !hasPermission ? (
    <EmptyState title={t('pages.clientes.noPermission')} compact />
  ) : isLoading ? (
    <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
      <Spin size="large" />
    </div>
  ) : error || !cliente ? (
    <EmptyState
      title={t('pages.clientes.notFound')}
      description={t('pages.clientes.notFoundEdit')}
    />
  ) : (
    <ClienteForm
      mode="edit"
      form={form}
      hideSubmit
      initialValues={cliente}
      onSubmit={handleSubmit}
      loading={guardando}
      onValuesChange={handleValuesChange}
    />
  );

  const canSave = hasPermission && !isLoading && !error && Boolean(cliente);
  const footer = [
    <Button key="cancel" onClick={requestClose} disabled={guardando}>
      {t('common.cancel')}
    </Button>,
    <Button
      key="submit"
      type="primary"
      loading={guardando}
      disabled={!canSave}
      onClick={() => form.submit()}
    >
      {t('common.saveChanges')}
    </Button>,
  ];
  const title = (
    <ClienteEditTitle
      cliente={cliente}
      clienteId={clienteId}
      editLabel={t('pages.clientes.edit')}
      codeLabel={t('pages.clientes.codigo')}
    />
  );

  const overlay = screens.lg ? (
    <Modal
      open={open}
      centered
      width={920}
      title={title}
      footer={footer}
      onCancel={requestClose}
      destroyOnHidden
      mask={{ closable: false }}
      classNames={{ body: 'cliente-edit-overlay__body' }}
      styles={{ body: modalBodyStyle }}
    >
      {content}
    </Modal>
  ) : (
    <Drawer
      open={open}
      placement="right"
      size={screens.sm ? 'min(640px, 100vw)' : '100%'}
      title={title}
      footer={footer}
      onClose={requestClose}
      destroyOnHidden
      mask={{ closable: false }}
      classNames={{ body: 'cliente-edit-overlay__body' }}
      styles={{ body: drawerBodyStyle }}
    >
      {content}
    </Drawer>
  );

  return (
    <>
      {overlay}
      <Modal
        open={confirmOpen}
        centered
        title={t('pages.clientes.unsavedChangesTitle')}
        onCancel={handleKeepEditing}
        destroyOnHidden
        footer={[
          <Button key="stay" onClick={handleKeepEditing}>
            {t('pages.clientes.unsavedChangesCancel')}
          </Button>,
          <Button key="discard" type="primary" danger onClick={handleDiscard}>
            {t('pages.clientes.unsavedChangesConfirm')}
          </Button>,
        ]}
      >
        <Typography.Paragraph style={{ marginBottom: 0 }}>
          {t('pages.clientes.unsavedChangesDescription')}
        </Typography.Paragraph>
      </Modal>
    </>
  );
}
